// ─────────────────────────────────────────────────────────────
// ScriptsTab — two sub-tabs: My Scripts, Starter Scripts.
// (Sub-tab order swapped 2026-05-15 — My Scripts is the default
// landing tab; the smart default below auto-switches new users
// with zero saved scripts to Starter Scripts on first mount so
// they don't stare at an empty My Scripts list.)
// (Community was promoted to a top-level tab on 2026-05-06 —
// see CommunityTab.tsx.)
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PublicOffOutlinedIcon from "@mui/icons-material/PublicOffOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { libraryScriptsApi, userScriptsApi } from "../../api/client";
import type { LibraryScript, LibraryScriptCategory, UserScript } from "../../api/client";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { PublishScriptDialog } from "./PublishScriptDialog";
import { ScriptMetadataEditDialog } from "./ScriptMetadataEditDialog";

// ── Sub-tab: Starter Scripts ──────────────────────────────────

function StarterScriptsSubTab({ onLoad }: { onLoad: (code: string, defaultImageId?: number | null, savedScriptId?: number | null, scriptTitle?: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<LibraryScriptCategory[]>([]);
  const [uncategorized, setUncategorized] = useState<LibraryScript[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    libraryScriptsApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setCategories(data.categories);
        setUncategorized(data.uncategorized);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load scripts");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const isEmpty = !loading && !error && categories.length === 0 && uncategorized.length === 0;

  // Flat ordered list: categories in order, uncategorized appended at the end.
  // Each entry carries the category name so the card can display its chip.
  const allScripts = useMemo<Array<{ script: LibraryScript; categoryName: string }>>(() => {
    const result: Array<{ script: LibraryScript; categoryName: string }> = [];
    for (const cat of categories) {
      for (const script of cat.scripts) {
        result.push({ script, categoryName: cat.name });
      }
    }
    for (const script of uncategorized) {
      result.push({ script, categoryName: "Other" });
    }
    return result;
  }, [categories, uncategorized]);

  const visibleScripts = useMemo(() => {
    if (activeCategory === "All") return allScripts;
    return allScripts.filter((item) => item.categoryName === activeCategory);
  }, [allScripts, activeCategory]);

  // Chip labels: "All" + one per category + "Other" if uncategorized exist
  const chipLabels = useMemo(() => {
    const labels = categories.map((cat) => cat.name);
    if (uncategorized.length > 0) labels.push("Other");
    return labels;
  }, [categories, uncategorized]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%", justifyContent: "center" }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading scripts...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", justifyContent: "center" }}>
        <Typography variant="body2" color="text.disabled">
          Could not load starter scripts.
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", justifyContent: "center" }}>
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "background.paper",
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No starter scripts yet. Ask an admin to add some.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Filter chip row — fixed, does not scroll */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          gap: 1,
          overflowX: "auto",
          overflowY: "hidden",
          // Hide scrollbar track but keep scroll functionality
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {["All", ...chipLabels].map((label) => (
          <Chip
            key={label}
            label={label}
            size="medium"
            variant={activeCategory === label ? "filled" : "outlined"}
            color={activeCategory === label ? "primary" : "default"}
            onClick={() => setActiveCategory(label)}
            sx={{
              flexShrink: 0,
              cursor: "pointer",
              height: 36,
              fontSize: "0.8125rem",
              "& .MuiChip-label": { px: 1.75 },
            }}
          />
        ))}
      </Box>

      {/* Scrollable script grid */}
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 2.5,
          }}
        >
          {visibleScripts.map(({ script, categoryName }) => {
            const tags = script.tags ?? [];
            const visibleTags = tags.slice(0, 2);
            const overflowCount = Math.max(0, tags.length - 2);
            const showCategoryChip = activeCategory === "All";
            const handleClick = () => onLoad(script.code, script.default_image_id, null, script.title);
            const tooltipContent = (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                  {script.title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Category:
                  </Typography>
                  <Chip
                    label={categoryName}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.625rem" }}
                  />
                </Box>
                <Divider />
                {script.description ? (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {script.description}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No description.
                  </Typography>
                )}
                {tags.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Tags:
                    </Typography>
                    {tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.625rem" }}
                      />
                    ))}
                  </Box>
                )}
                {script.default_image_id != null && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500, mt: 0.25 }}
                  >
                    Loads with a paired image
                  </Typography>
                )}
              </Box>
            );
            return (
              <Tooltip
                key={script.id}
                title={tooltipContent}
                arrow
                placement="top"
                enterDelay={400}
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 360,
                      p: 1.5,
                      bgcolor: "background.paper",
                      color: "text.primary",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: 4,
                      // Let mouse events fall through so adjacent cards
                      // (esp. the one ABOVE the hovered card, since
                      // placement="top") remain clickable while the
                      // tooltip is visible. Greg 2026-05-15.
                      pointerEvents: "none",
                    },
                  },
                  arrow: {
                    sx: (theme) => ({
                      color: theme.palette.background.paper,
                      pointerEvents: "none",
                      "&::before": {
                        border: `1px solid ${theme.palette.divider}`,
                      },
                    }),
                  },
                }}
              >
              <Card
                elevation={0}
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                  }
                }}
                aria-label={`Load ${script.title}`}
                sx={(theme) => ({
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  p: 2,
                  transition: "background-color 200ms cubic-bezier(0.2,0,0,1), box-shadow 200ms cubic-bezier(0.2,0,0,1), border-color 200ms cubic-bezier(0.2,0,0,1)",
                  "&:hover": {
                    bgcolor: "action.hover",
                    boxShadow: 4,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    "& .script-card-action": { opacity: 1 },
                  },
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                  },
                })}
              >
                {/* Title row — title + optional category chip */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {script.title}
                  </Typography>
                  {showCategoryChip && (
                    <Chip
                      label={categoryName}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.625rem", flexShrink: 0, mt: 0.125 }}
                    />
                  )}
                </Box>

                <Divider sx={{ my: 1.25 }} />

                {/* Body — description + footer */}
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 1.25 }}>
                  {script.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {script.description}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mt: "auto",
                      pt: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", minWidth: 0 }}>
                      {visibleTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      ))}
                      {overflowCount > 0 && (
                        <Chip
                          label={`+${overflowCount}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      )}
                    </Box>
                    <Box
                      className="script-card-action"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        color: "primary.main",
                        opacity: 0,
                        transition: "opacity 200ms cubic-bezier(0.2,0,0,1)",
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.6875rem" }}>
                        Load
                      </Typography>
                      <ArrowForwardOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                </Box>
              </Card>
              </Tooltip>
            );
          })}

          {visibleScripts.length === 0 && (
            <Box
              sx={{
                gridColumn: "1 / -1",
                p: 4,
                textAlign: "center",
                bgcolor: "background.paper",
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No scripts in this category.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Sub-tab: My Scripts ───────────────────────────────────────

function MyScriptsSubTab({
  onLoad,
  onInitialLoad,
}: {
  onLoad: (code: string, defaultImageId?: number | null, savedScriptId?: number | null, scriptTitle?: string) => void;
  /**
   * Fires exactly once after the first list fetch resolves successfully.
   * The parent uses this to drive the "0 scripts → auto-switch to Starter
   * Scripts" smart-default behavior added 2026-05-15.
   */
  onInitialLoad?: (scriptCount: number) => void;
}) {
  const [scripts, setScripts] = useState<UserScript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isAuthenticated] = useState(() => !!localStorage.getItem("auth_token"));
  // One-shot guard for onInitialLoad — only fire after the very first
  // successful fetch, not after subsequent refetches (e.g. delete revert).
  const initialLoadReportedRef = useRef(false);
  // Metadata edit dialog (Greg, 2026-05-14). Replaces the previous inline
  // title/description edit so authors can also swap the paired reference
  // image in one place. Null = dialog closed.
  const [editDialogScript, setEditDialogScript] = useState<UserScript | null>(null);
  // Publish-to-Community dialog (Greg, 2026-05-13). Holds the script the user
  // is publishing — null when the dialog is closed. Unpublish stays one-click
  // and does NOT route through this dialog.
  const [publishDialogScript, setPublishDialogScript] = useState<UserScript | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    userScriptsApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setScripts(data.scripts);
        if (!initialLoadReportedRef.current) {
          initialLoadReportedRef.current = true;
          onInitialLoad?.(data.scripts.length);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load scripts.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // onInitialLoad intentionally omitted — fires once, captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  async function handleDelete(id: number, title: string) {
    // Optimistic removal
    setScripts((prev) => prev.filter((s) => s.id !== id));
    try {
      await userScriptsApi.delete(id);
    } catch {
      // Restore on failure
      setError(`Failed to delete "${title}". Please try again.`);
      userScriptsApi.list().then((data) => setScripts(data.scripts)).catch(() => {/* ignore */});
    }
  }

  // Publish-to-Community share-toggle click handler. Two paths:
  //   • public → private (unpublish): one-click, no dialog. Optimistic update,
  //     revert on failure. Unchanged from the original behavior.
  //   • private → public (publish): opens PublishScriptDialog so the author
  //     can pair a sample image with the script. Dialog calls handlePublished
  //     via onSuccess once the backend confirms.
  async function handleShareToggleClick(script: UserScript) {
    if (script.is_public) {
      // Optimistic unpublish
      setScripts((prev) =>
        prev.map((s) => (s.id === script.id ? { ...s, is_public: false } : s))
      );
      try {
        const updated = await userScriptsApi.publish(script.id, false);
        setScripts((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        setSnackbar({
          open: true,
          message: `"${script.title}" removed from Community.`,
          severity: "success",
        });
      } catch (err) {
        // Revert
        setScripts((prev) =>
          prev.map((s) => (s.id === script.id ? { ...s, is_public: true } : s))
        );
        setSnackbar({
          open: true,
          message: err instanceof Error ? err.message : "Failed to update visibility.",
          severity: "error",
        });
      }
      return;
    }

    // Private → public: defer to the dialog. No API call yet.
    setPublishDialogScript(script);
  }

  // Called by PublishScriptDialog after the publish API succeeds. The dialog
  // closes itself via onClose; we update local state with the server truth.
  function handlePublishSuccess(updated: UserScript) {
    setScripts((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    setPublishDialogScript(null);
    setSnackbar({
      open: true,
      message: `"${updated.title}" published to Community.`,
      severity: "success",
    });
  }

  // Edit dialog handlers. The dialog itself drives the PUT; we just merge
  // the server-returned script back into local state on success.
  function openEditDialog(script: UserScript) {
    setEditDialogScript(script);
  }

  function handleEditSuccess(updated: UserScript) {
    setScripts((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    setEditDialogScript(null);
    setSnackbar({
      open: true,
      message: "Script updated.",
      severity: "success",
    });
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
          Sign in to save and load your scripts.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%", justifyContent: "center" }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading scripts...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
        <Typography variant="body2" color="error.main" sx={{ textAlign: "center" }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, overflow: "auto", height: "100%" }}>
        {scripts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", bgcolor: "background.paper", borderRadius: 1, border: "1px dashed", borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              No saved scripts yet. Use "Save" in the Python editor to save your work here.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 2.5,
            }}
          >
            {scripts.map((script) => {
              const handleCardClick = () => {
                onLoad(script.code, script.default_image_id ?? null, script.id, script.title);
              };
              return (
                <Card
                  key={script.id}
                  elevation={0}
                  role="button"
                  tabIndex={0}
                  onClick={handleCardClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCardClick();
                    }
                  }}
                  aria-label={`Load ${script.title}`}
                  sx={(theme) => ({
                    bgcolor: "background.paper",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                    transition: "background-color 200ms cubic-bezier(0.2,0,0,1), box-shadow 200ms cubic-bezier(0.2,0,0,1), border-color 200ms cubic-bezier(0.2,0,0,1)",
                    "&:hover": {
                      bgcolor: "action.hover",
                      boxShadow: 4,
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "2px",
                    },
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {script.title}
                    </Typography>
                    {script.is_public && (
                      <Chip
                        label="Community"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.625rem", flexShrink: 0, mt: 0.125 }}
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 1.25 }} />

                  <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 1.25 }}>
                    {script.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {script.description}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mt: "auto",
                        pt: 1,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        Updated {formatRelativeTime(script.updated_at)}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
                        <Tooltip title="Edit title, description, or paired image">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(script); }}
                            aria-label={`Edit ${script.title}`}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={script.is_public ? "Remove from community" : "Share with community"}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleShareToggleClick(script); }}
                            aria-label={script.is_public ? `Unpublish ${script.title}` : `Publish ${script.title}`}
                            sx={{ color: script.is_public ? "primary.main" : "text.disabled" }}
                          >
                            {script.is_public
                              ? <PublicOutlinedIcon sx={{ fontSize: 16 }} />
                              : <PublicOffOutlinedIcon sx={{ fontSize: 16 }} />
                            }
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete script">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDelete(script.id, script.title); }}
                            aria-label={`Delete ${script.title}`}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PublishScriptDialog
        open={publishDialogScript !== null}
        scriptId={publishDialogScript?.id ?? null}
        scriptTitle={publishDialogScript?.title ?? null}
        defaultImageId={publishDialogScript?.default_image_id ?? null}
        onClose={() => setPublishDialogScript(null)}
        onSuccess={handlePublishSuccess}
      />

      <ScriptMetadataEditDialog
        open={editDialogScript !== null}
        script={editDialogScript}
        onClose={() => setEditDialogScript(null)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}

// ── Main component ────────────────────────────────────────────

interface ScriptsTabProps {
  /** Called when the user wants to load a script into the editor. Receives the script code, optional paired image id, and optional saved script id. */
  onLoadScript: (code: string, defaultImageId?: number | null, savedScriptId?: number | null, scriptTitle?: string) => void;
  /**
   * Optional initial sub-tab index (0 = My Scripts, 1 = Starter Scripts).
   * Order swapped 2026-05-15 — My Scripts is now the default landing tab.
   * Used by the parent to land on a specific sub-tab after navigating from
   * elsewhere (e.g. Fork from CommunityTab → My Scripts).
   * Increment `initialSubTabKey` alongside the value to force a re-apply
   * if the same value is set twice in a row.
   * When this prop is OMITTED, the smart-default kicks in: new users with
   * zero saved scripts land on Starter Scripts; everyone else stays on
   * My Scripts. An explicit value from the parent always wins.
   */
  initialSubTab?: 0 | 1;
  initialSubTabKey?: number;
}

export function ScriptsTab({ onLoadScript, initialSubTab, initialSubTabKey = 0 }: ScriptsTabProps) {
  // Default landing tab is My Scripts (index 0). If the parent passed an
  // explicit initialSubTab, honor it — that's a navigation intent (e.g.
  // CommunityTab → My Scripts) and must not be overridden.
  const [subTab, setSubTab] = useState<0 | 1>(initialSubTab ?? 0);

  // Track whether the parent explicitly set the sub-tab and whether the
  // user has manually clicked one of the sub-tabs. Either condition
  // disables the smart-default auto-switch (we never override user intent
  // or explicit parent navigation).
  const parentSetSubTabRef = useRef<boolean>(initialSubTab !== undefined);
  const userPickedSubTabRef = useRef<boolean>(false);

  // When the parent signals a new initial sub-tab (via a bumped key), apply it.
  useEffect(() => {
    if (initialSubTab === undefined) return;
    setSubTab(initialSubTab);
    parentSetSubTabRef.current = true;
  }, [initialSubTab, initialSubTabKey]);

  // Smart default: if the user has 0 saved scripts on first mount AND has
  // NOT manually picked a sub-tab AND the parent did NOT pass an explicit
  // initialSubTab, auto-switch to Starter Scripts so the user doesn't land
  // on an empty tab. Fires only once — MyScriptsSubTab's onInitialLoad
  // callback is gated to fire exactly once after the first list fetch.
  function handleMyScriptsInitialLoad(count: number) {
    if (count !== 0) return;
    if (parentSetSubTabRef.current) return;
    if (userPickedSubTabRef.current) return;
    setSubTab(1);
  }

  function handleTabChange(_: unknown, v: number) {
    userPickedSubTabRef.current = true;
    setSubTab(v as 0 | 1);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Tabs
        value={subTab}
        onChange={handleTabChange}
        centered
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 1,
          flexShrink: 0,
          minHeight: 40,
          "& .MuiTab-root": { minHeight: 40 },
          "& .MuiTabs-flexContainer": { justifyContent: "center" },
        }}
      >
        <Tab label="My Scripts" />
        <Tab label="Starter Scripts" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {subTab === 0 && (
          <MyScriptsSubTab
            onLoad={onLoadScript}
            onInitialLoad={handleMyScriptsInitialLoad}
          />
        )}
        {subTab === 1 && <StarterScriptsSubTab onLoad={onLoadScript} />}
      </Box>
    </Box>
  );
}
